import { useState, useEffect, useMemo, Fragment } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { salesAPI, productsAPI, customersAPI, batchesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../components/ui/command';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { printTransaction } from '../lib/printDoc';
import LineItemsGrid from '../components/lineitems/LineItemsGrid';
import BatchPickerRow from '../components/lineitems/BatchPickerRow';
import MobileSaleTotalBar from '../components/lineitems/MobileSaleTotalBar';
import BarcodeScanButton from '../components/scanner/BarcodeScanButton';
import { mergeScannedLine, unmergeScannedLine } from '../components/lineitems/mergeScannedLine';
import {
    VAT_PROFILES, VAT_PROFILE_KEYS,
    resolveVatRate, DEFAULT_VAT_PROFILE_KEY, DEFAULT_VAT_RATE,
} from '../lib/vatProfiles';
import {
    Plus,
    Trash2,
    ArrowLeft,
    Loader2,
    Banknote,
    CreditCard,
    Building2,
    FileCheck,
    Clock,
    MoreHorizontal,
    Calendar as CalendarIcon,
    Printer,
    Save,
    Search,
    Check,
    FileText as FileTextIcon,
    Boxes,
} from 'lucide-react';

const VAT_RATE = 20;  // legacy fallback — invoice-level VAT (see `invoiceVatRate` state) overrides this at runtime

const paymentMethods = [
    { value: 'cash', label: 'Cash', icon: Banknote, color: 'bg-[#2A9D8F]' },
    { value: 'card', label: 'Card', icon: CreditCard, color: 'bg-[#264653]' },
    { value: 'bank_transfer', label: 'Bank Transfer', icon: Building2, color: 'bg-[#E9C46A]' },
    { value: 'cheque', label: 'Cheque', icon: FileCheck, color: 'bg-[#8B5CF6]' },
    { value: 'due', label: 'Due', icon: Clock, color: 'bg-[#E76F51]' },
    { value: 'others', label: 'Others', icon: MoreHorizontal, color: 'bg-[#6B7280]' },
];

const fmt = (v) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);

/**
 * Shared form used for:
 *  - Invoice creation (mode='invoice')
 *  - Quotation creation (mode='quotation')
 *  - Editing an existing draft / quotation (editId provided via :id route param)
 */
const CreateInvoice = ({ mode = 'invoice' }) => {
    const navigate = useNavigate();
    const { id: editId } = useParams();
    const [searchParams] = useSearchParams();
    // When the user clicks "Finalize" on a quotation/draft we navigate here with
    // ?finalize=1 — the form should switch into invoice mode (show payment fields
    // and the "Finalize Invoice & Print" button) instead of staying as a quotation.
    const finalizeMode = searchParams.get('finalize') === '1';

    const [docNumber, setDocNumber] = useState('');
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [editingDoc, setEditingDoc] = useState(null);
    const [docMode, setDocMode] = useState(mode); // becomes 'quotation' or 'invoice' (sale type stays invoice)

    // Customer
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [companyAddress, setCompanyAddress] = useState('');
    const [contactPerson, setContactPerson] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    const [emailAddress, setEmailAddress] = useState('');

    // Line items
    const [lineItems, setLineItems] = useState([]);

    // Discount + delivery
    const [discountType, setDiscountType] = useState('amount'); // amount | percent
    const [discountValue, setDiscountValue] = useState(0);
    const [deliveryCharge, setDeliveryCharge] = useState(0);
    const [totalCartons, setTotalCartons] = useState('');

    // Invoice-level VAT (Jul 2026) — pre-loaded from the selected
    // customer's `default_vat_rate` / `vat_type`. Operator can override
    // before validation via the VAT selector in the totals card. When
    // it changes, every line's `vat_rate` snaps to the new rate.
    const [invoiceVatType, setInvoiceVatType] = useState(DEFAULT_VAT_PROFILE_KEY);
    const [invoiceVatRate, setInvoiceVatRate] = useState(DEFAULT_VAT_RATE);

    // Payment (only used when finalizing into an invoice)
    const [isSplit, setIsSplit] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [chequeDepositDate, setChequeDepositDate] = useState(null);
    const [chequeBankName, setChequeBankName] = useState('');
    const [chequeNumber, setChequeNumber] = useState('');
    const [payments, setPayments] = useState([]);

    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);

    const isQuotation = docMode === 'quotation';
    const isEditing = !!editId;
    // Show the "not a paid VAT invoice" disclaimer for both drafts and
    // quotations. Uses the ORIGINAL saved status (before ?finalize=1
    // flips docMode into invoice), so a draft being finalized still shows
    // it correctly, but a fresh invoice creation flow does not.
    const editingIsDraftOrQuote =
        !!editingDoc && (editingDoc.status === 'draft' || editingDoc.status === 'quotation');
    const showVatDisclaimer = isQuotation || editingIsDraftOrQuote;

    // Bootstrap: load products, customers, and either next-number or existing doc
    useEffect(() => {
        productsAPI.getAll().then((r) => setProducts(r.data)).catch(() => {});
        customersAPI.getAll().then((r) => setCustomers(r.data)).catch(() => {});

        if (editId) {
            salesAPI.get(editId).then(({ data }) => {
                setEditingDoc(data);
                // If the URL carries ?finalize=1 → switch the form into invoice mode
                // (shows payment block + the Finalize Invoice button).
                setDocMode(finalizeMode ? 'invoice' : (data.status === 'quotation' ? 'quotation' : 'invoice'));
                setDocNumber(data.invoice_number || '');
                setSelectedCustomerId(data.customer_id || '');
                setCompanyName(data.company_name || '');
                setCompanyAddress(data.company_address || '');
                setContactPerson(data.contact_person || '');
                setMobileNumber(data.mobile_number || '');
                setEmailAddress(data.email_address || '');
                setNotes(data.notes || '');
                setDiscountType(data.discount_type || 'amount');
                setDiscountValue(data.discount_value || 0);
                setDeliveryCharge(data.delivery_charge || 0);
                setTotalCartons(data.total_cartons != null ? String(data.total_cartons) : '');
                // Load invoice-level VAT if present on the saved sale
                if (data.vat_rate != null) {
                    setInvoiceVatRate(Number(data.vat_rate));
                }
                setLineItems(
                    (data.line_items || []).map((li) => ({
                        product_id: li.product_id || '',
                        product_name: li.product_name || '',
                        sku: li.sku || '',
                        quantity: li.quantity,
                        unit_price: li.unit_price,
                        vat_rate: li.vat_rate != null ? Number(li.vat_rate) : undefined,
                        line_total: li.line_total,
                    }))
                );
            }).catch(() => toast.error('Failed to load draft'));
        } else {
            // Preview next number
            const fetcher = mode === 'quotation' ? salesAPI.nextQuotationNumber : salesAPI.nextInvoiceNumber;
            fetcher().then((r) => setDocNumber(r.data.next)).catch(() => {});
        }
    }, [editId, mode]);

    const handleCustomerSelect = (id) => {
        setSelectedCustomerId(id);
        setCustomerSearchOpen(false);
        if (id === '__new__' || !id) {
            return;
        }
        const c = customers.find((x) => x.id === id);
        if (c) {
            setCompanyName(c.company_name || '');
            setCompanyAddress(c.company_address || '');
            setContactPerson(c.contact_person || '');
            setMobileNumber(c.mobile_number || '');
            setEmailAddress(c.email_address || '');
            // Pre-fill invoice-level VAT from the customer's stored profile.
            // Legacy customers without these fields fall back to FR_STANDARD 20%.
            const nextType = c.vat_type || DEFAULT_VAT_PROFILE_KEY;
            const nextRate = c.default_vat_rate != null
                ? Number(c.default_vat_rate)
                : resolveVatRate(nextType);
            setInvoiceVatType(nextType);
            setInvoiceVatRate(nextRate);
            // Snap existing line items to the new rate so totals stay consistent.
            setLineItems((prev) => prev.map((l) => ({ ...l, vat_rate: nextRate })));
        }
    };

    const addLineItem = () => {
        setLineItems([
            ...lineItems,
            { product_id: '', product_name: '', sku: '', quantity: 1, unit_price: 0, line_total: 0 },
        ]);
    };

    const updateLineItem = (idx, field, value) => {
        const next = [...lineItems];
        next[idx] = { ...next[idx], [field]: value };
        if (field === 'product_id') {
            const p = products.find((x) => x.id === value);
            if (p) {
                next[idx].product_name = p.name;
                next[idx].sku = p.sku;
                next[idx].unit_price = Number(p.price) || 0;
                next[idx].vat_rate = p.vat_rate != null ? Number(p.vat_rate) : invoiceVatRate;
                next[idx].batch_required = !!p.batch_required;
                next[idx].batch_allocations = null; // recompute when product changes
            }
        }
        const q = parseFloat(next[idx].quantity) || 0;
        const u = parseFloat(next[idx].unit_price) || 0;
        next[idx].line_total = +(q * u).toFixed(2);
        // Invalidate allocations when quantity changes — let auto-FEFO re-allocate
        if (field === 'quantity') {
            next[idx].batch_allocations = null;
        }
        setLineItems(next);
    };

    // Auto-allocate FEFO whenever a batch-tracked line has no allocations yet
    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            for (let i = 0; i < lineItems.length; i++) {
                const li = lineItems[i];
                const qty = parseFloat(li.quantity) || 0;
                if (!li.product_id || !li.batch_required || qty <= 0) continue;
                if (li.batch_allocations && li.batch_allocations.length > 0) continue;
                try {
                    const { data } = await batchesAPI.forProduct(li.product_id, true);
                    if (cancelled) return;
                    // Greedy FEFO until qty met
                    let remaining = qty;
                    const alloc = [];
                    for (const b of data) {
                        if (remaining <= 0) break;
                        const take = Math.min(Number(b.quantity_remaining), remaining);
                        if (take <= 0) continue;
                        alloc.push({
                            batch_id: b.id, batch_number: b.batch_number,
                            quantity: +take.toFixed(4), expiry_date: b.expiry_date,
                        });
                        remaining = +(remaining - take).toFixed(4);
                    }
                    setLineItems((curr) => {
                        const copy = [...curr];
                        if (!copy[i]) return curr;
                        copy[i] = {
                            ...copy[i],
                            batch_allocations: alloc,
                            batch_shortfall: remaining > 0 ? remaining : 0,
                            batch_options: data,
                        };
                        return copy;
                    });
                } catch {
                    /* silent */
                }
            }
        };
        run();
        return () => { cancelled = true; };
    }, [lineItems]);

    const removeLineItem = (idx) => {
        setLineItems(lineItems.filter((_, i) => i !== idx));
    };

    // === Math === (per-line VAT aware; falls back to flat VAT_RATE if a line has no rate)
    const subtotal = useMemo(
        () => +lineItems.reduce((s, l) => s + (parseFloat(l.line_total) || 0), 0).toFixed(2),
        [lineItems]
    );
    const discountAmount = useMemo(() => {
        const v = parseFloat(discountValue) || 0;
        if (discountType === 'percent') return +((subtotal * v) / 100).toFixed(2);
        return +Math.min(v, subtotal).toFixed(2);
    }, [discountType, discountValue, subtotal]);
    const vatBreakdown = useMemo(() => {
        // Distribute discount proportionally then VAT per line
        const breakdown = {};
        if (subtotal <= 0) return breakdown;
        for (const l of lineItems) {
            const lineTotal = parseFloat(l.line_total) || 0;
            const rate = l.vat_rate != null ? parseFloat(l.vat_rate) : invoiceVatRate;
            const lineDiscount = +((lineTotal * discountAmount) / subtotal).toFixed(2);
            const taxable = Math.max(0, lineTotal - lineDiscount);
            const lineVat = +(taxable * rate / 100).toFixed(2);
            const key = `${rate}`;
            breakdown[key] = +((breakdown[key] || 0) + lineVat).toFixed(2);
        }
        return breakdown;
    }, [lineItems, discountAmount, subtotal]);
    const vatAmount = useMemo(
        () => +Object.values(vatBreakdown).reduce((s, v) => s + v, 0).toFixed(2),
        [vatBreakdown]
    );
    const taxable = useMemo(() => Math.max(0, subtotal - discountAmount), [subtotal, discountAmount]);
    const delivery = useMemo(() => parseFloat(deliveryCharge) || 0, [deliveryCharge]);
    const totalAmount = useMemo(
        () => +(taxable + vatAmount + delivery).toFixed(2),
        [taxable, vatAmount, delivery]
    );

    // === Split payment helpers ===
    // Each split-payment row carries a stable `_key` (uuid) so React can
    // reconcile even when the caller reorders or deletes rows. The key is
    // stripped before submitting to the backend.
    const addPayment = () => setPayments([...payments, {
        _key: `pmt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        method: '', amount: '', cheques: [],
    }]);
    const removePayment = (i) => setPayments(payments.filter((_, idx) => idx !== i));
    const updatePayment = (i, field, value) => {
        const next = [...payments];
        next[i][field] = value;
        if (field === 'method' && value !== 'cheque') next[i].cheques = [];
        setPayments(next);
    };
    const addCheque = (pIdx) => {
        const next = [...payments];
        next[pIdx].cheques.push({ bank_name: '', cheque_number: '', deposit_date: null, amount: '' });
        setPayments(next);
    };
    const removeCheque = (pIdx, cIdx) => {
        const next = [...payments];
        next[pIdx].cheques = next[pIdx].cheques.filter((_, i) => i !== cIdx);
        setPayments(next);
    };
    const updateCheque = (pIdx, cIdx, field, value) => {
        const next = [...payments];
        next[pIdx].cheques[cIdx][field] = value;
        setPayments(next);
    };

    const buildBasePayload = () => ({
        type: 'invoice',
        line_items: lineItems.map((l) => ({
            product_id: l.product_id || null,
            product_name: l.product_name,
            sku: l.sku || '',
            quantity: parseFloat(l.quantity) || 0,
            unit_price: parseFloat(l.unit_price) || 0,
            // line_total is auto-computed server-side; vat_rate is per-line
            vat_rate: l.vat_rate != null ? parseFloat(l.vat_rate) : invoiceVatRate,
            batch_allocations: l.batch_required && l.batch_allocations && l.batch_allocations.length > 0
                ? l.batch_allocations.map((a) => ({ batch_id: a.batch_id, quantity: a.quantity }))
                : null,
        })),
        vat_rate: invoiceVatRate,  // invoice-level default; per-line rates in `line_items` take precedence
        amount: totalAmount,
        subtotal,
        vat_amount: vatAmount,
        discount_type: discountType,
        discount_value: parseFloat(discountValue) || 0,
        discount_amount: discountAmount,
        delivery_charge: delivery,
        // Logistics — send only when the user entered a value; empty
        // string is coerced to `null` so drafts stay clean.
        total_cartons: totalCartons === '' ? null : Math.max(0, parseInt(totalCartons, 10) || 0),
        notes,
        customer_id: selectedCustomerId && selectedCustomerId !== '__new__' ? selectedCustomerId : null,
        company_name: companyName,
        company_address: companyAddress,
        contact_person: contactPerson,
        mobile_number: mobileNumber,
        email_address: emailAddress,
    });

    const validateLineItems = () => {
        if (lineItems.length === 0) {
            toast.error('Add at least one line item');
            return false;
        }
        for (const li of lineItems) {
            if (!li.product_name || !li.quantity || !li.unit_price) {
                toast.error('Each line item needs product, quantity & unit price');
                return false;
            }
        }
        return true;
    };

    const collectPayments = () => {
        if (isSplit) {
            if (payments.length < 2) {
                toast.error('Split payment requires at least 2 methods');
                return null;
            }
            const paymentTotal = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
            if (Math.abs(paymentTotal - totalAmount) > 0.01) {
                toast.error(`Payments (€${paymentTotal.toFixed(2)}) must equal invoice total (€${totalAmount.toFixed(2)})`);
                return null;
            }
            for (let i = 0; i < payments.length; i++) {
                const p = payments[i];
                if (!p.method || !p.amount) {
                    toast.error(`Payment ${i + 1} incomplete`);
                    return null;
                }
                if (p.method === 'cheque') {
                    const lineCheques = Array.isArray(p.cheques) ? p.cheques : [];
                    if (!lineCheques.length) {
                        toast.error(`Payment ${i + 1}: add cheques`);
                        return null;
                    }
                    const chq = lineCheques.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
                    if (Math.abs(chq - parseFloat(p.amount)) > 0.01) {
                        toast.error(`Cheques (€${chq.toFixed(2)}) must equal payment (€${parseFloat(p.amount).toFixed(2)})`);
                        return null;
                    }
                    for (const c of lineCheques) {
                        if (!c.bank_name || !c.cheque_number || !c.deposit_date || !c.amount) {
                            toast.error('All cheque fields required');
                            return null;
                        }
                    }
                }
            }
            return {
                payments: payments.map((p) => ({
                    method: p.method,
                    amount: parseFloat(p.amount),
                    cheques: p.method === 'cheque'
                        ? (p.cheques || []).map((c) => ({
                              bank_name: c.bank_name,
                              cheque_number: c.cheque_number,
                              // `c.deposit_date` may be a Date (just-picked) OR
                              // a yyyy-MM-dd string if the row was hydrated from
                              // an existing draft — handle both safely.
                              deposit_date: c.deposit_date instanceof Date
                                  ? format(c.deposit_date, 'yyyy-MM-dd')
                                  : String(c.deposit_date),
                              amount: parseFloat(c.amount),
                          }))
                        : null,
                })),
            };
        }
        if (!paymentMethod) {
            toast.error('Select a payment method');
            return null;
        }
        const out = { payment_method: paymentMethod };
        if (paymentMethod === 'cheque') {
            if (!chequeBankName || !chequeNumber || !chequeDepositDate) {
                toast.error('Cheque details required');
                return null;
            }
            out.cheque_bank_name = chequeBankName;
            out.cheque_number = chequeNumber;
            out.cheque_deposit_date = chequeDepositDate instanceof Date
                ? format(chequeDepositDate, 'yyyy-MM-dd')
                : String(chequeDepositDate);
        }
        return out;
    };

    // === Save as draft (no payment / no stock impact) ===
    const handleSaveDraft = async () => {
        if (!validateLineItems()) return;
        setSavingDraft(true);
        try {
            const payload = { ...buildBasePayload(), status: 'draft' };
            if (isEditing) {
                await salesAPI.update(editId, payload);
                toast.success('Draft updated');
            } else {
                await salesAPI.create(payload);
                toast.success('Draft saved');
            }
            navigate('/drafts');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save draft');
        } finally {
            setSavingDraft(false);
        }
    };

    // === Save as Quotation (no payment / no stock impact) ===
    const handleSaveQuotation = async () => {
        if (!validateLineItems()) return;
        setLoading(true);
        try {
            const payload = { ...buildBasePayload(), status: 'quotation' };
            let data;
            if (isEditing) {
                const r = await salesAPI.update(editId, payload);
                data = r.data;
            } else {
                const r = await salesAPI.create(payload);
                data = r.data;
            }
            toast.success(`Quotation ${data.invoice_number} saved`, {
                action: { label: 'Print PDF', onClick: () => printTransaction(data, { language: data.customer_document_language || 'fr' }) },
                duration: 8000,
            });
            setTimeout(() => printTransaction(data, { language: data.customer_document_language || 'fr' }), 400);
            navigate('/drafts');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save quotation');
        } finally {
            setLoading(false);
        }
    };

    // === Finalize / Submit invoice ===
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateLineItems()) return;
        const payInfo = collectPayments();
        if (!payInfo) return;

        const payload = { ...buildBasePayload(), status: 'final', ...payInfo };

        setLoading(true);
        try {
            let data;
            if (isEditing) {
                // Finalize the existing draft / quotation - assigns new UC… number, deducts stock
                const r = await salesAPI.finalize(editId, payload);
                data = r.data;
            } else {
                const r = await salesAPI.create(payload);
                data = r.data;
            }
            toast.success(`Invoice ${data.invoice_number} created!`, {
                action: { label: 'Print PDF', onClick: () => printTransaction(data, { language: data.customer_document_language || 'fr' }) },
                duration: 8000,
            });
            setTimeout(() => printTransaction(data, { language: data.customer_document_language || 'fr' }), 400);
            navigate('/sales');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create invoice');
        } finally {
            setLoading(false);
        }
    };

    const filteredCustomerLabel = customers.find((c) => c.id === selectedCustomerId)?.company_name;

    return (
        <div data-testid="create-invoice-page" className="space-y-6 animate-fadeIn pb-24 md:pb-0">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <Button
                        variant="ghost"
                        onClick={() => navigate(isQuotation ? '/sales' : '/sales')}
                        data-testid="back-to-choice"
                        className="-ml-3 mb-1 text-gray-500 hover:text-[#1A1A1A]"
                    >
                        <ArrowLeft size={16} className="mr-1" /> Back
                    </Button>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">
                        {isEditing
                            ? `Edit ${editingDoc?.status === 'quotation' ? 'Quotation' : 'Draft'}`
                            : isQuotation
                                ? 'Create a Quotation'
                                : 'Create Invoice'}
                    </h1>
                    <p className="text-gray-500 mt-1">
                        {isQuotation
                            ? 'Formal offer · 20% VAT · stock untouched until finalized'
                            : 'Itemized billing · 20% VAT · auto-numbering'}
                    </p>
                </div>
                <div className="px-4 py-2 bg-[#1A1A1A] text-white rounded-lg">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400">
                        {isQuotation ? 'Quotation No.' : 'Invoice No.'}
                    </p>
                    <p className="font-mono font-bold text-lg" data-testid="auto-invoice-number">
                        {docNumber || '...'}
                    </p>
                </div>
            </div>

            {showVatDisclaimer && (
                <div data-testid="quotation-disclaimer" className="text-xs italic text-[#8B5CF6] bg-[#8B5CF6]/10 rounded-md px-4 py-2">
                    Ce document n'est pas une facture acquittée de la TVA
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Customer */}
                <Card className="border-0 shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-base">Bill To</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2 md:col-span-2">
                                <Label>Customer</Label>
                                <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            role="combobox"
                                            data-testid="customer-search-trigger"
                                            className="w-full justify-between h-11 bg-[#F5F5F5] font-normal"
                                        >
                                            <span className="flex items-center gap-2 truncate">
                                                <Search size={14} className="text-gray-400 shrink-0" />
                                                {filteredCustomerLabel || (companyName || 'Search or add new customer...')}
                                            </span>
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                                        <Command>
                                            <CommandInput placeholder="Search customers..." data-testid="customer-search-input" />
                                            <CommandList>
                                                <CommandEmpty>No customer found.</CommandEmpty>
                                                <CommandGroup>
                                                    <CommandItem
                                                        value="__new__"
                                                        onSelect={() => handleCustomerSelect('__new__')}
                                                        data-testid="customer-new"
                                                    >
                                                        <Plus size={14} className="mr-2 text-[#C08478]" />
                                                        New customer (enter details below)
                                                    </CommandItem>
                                                    {customers.map((c) => (
                                                        <CommandItem
                                                            key={c.id}
                                                            value={`${c.company_name} ${c.contact_person} ${c.mobile_number} ${c.email_address}`}
                                                            onSelect={() => handleCustomerSelect(c.id)}
                                                            data-testid={`customer-opt-${c.id}`}
                                                        >
                                                            <Check
                                                                size={14}
                                                                className={`mr-2 ${selectedCustomerId === c.id ? 'opacity-100' : 'opacity-0'}`}
                                                            />
                                                            <div className="flex-1 truncate">
                                                                <div className="font-medium">{c.company_name}</div>
                                                                {c.contact_person && (
                                                                    <div className="text-xs text-gray-500">{c.contact_person}</div>
                                                                )}
                                                            </div>
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="space-y-2">
                                <Label>Company Name</Label>
                                <Input
                                    data-testid="company-name"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    className="h-11 bg-[#F5F5F5]"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Contact Person</Label>
                                <Input
                                    value={contactPerson}
                                    onChange={(e) => setContactPerson(e.target.value)}
                                    className="h-11 bg-[#F5F5F5]"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Mobile</Label>
                                <Input
                                    value={mobileNumber}
                                    onChange={(e) => setMobileNumber(e.target.value)}
                                    className="h-11 bg-[#F5F5F5]"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    value={emailAddress}
                                    onChange={(e) => setEmailAddress(e.target.value)}
                                    className="h-11 bg-[#F5F5F5]"
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label>Address</Label>
                                <Textarea
                                    value={companyAddress}
                                    onChange={(e) => setCompanyAddress(e.target.value)}
                                    rows={2}
                                    className="bg-[#F5F5F5]"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Line items */}
                <Card className="border-0 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0">
                        <CardTitle className="text-base">Line Items</CardTitle>
                        <BarcodeScanButton
                            testId="invoice-scan-btn"
                            onAddLine={(product, qty) =>
                                setLineItems((curr) => mergeScannedLine(curr, product, qty))
                            }
                            onUndoLine={(product, qty) =>
                                setLineItems((curr) => unmergeScannedLine(curr, product, qty))
                            }
                            summary={{
                                productCount: lineItems.length,
                                totalQty: lineItems.reduce(
                                    (s, l) => s + (Number(l.quantity) || 0), 0,
                                ),
                                subtotal,
                                vatAmount,
                                total: totalAmount,
                                currencyFmt: fmt,
                            }}
                        />
                    </CardHeader>
                    <CardContent>
                        <LineItemsGrid
                            value={lineItems}
                            onChange={setLineItems}
                            products={products}
                            currencyFmt={fmt}
                            autoFocusFirst
                            onSubmit={() => handleSubmit({ preventDefault: () => {} })}
                            renderRowExtra={(li, idx) => (
                                li.batch_required ? (
                                    <TableRow data-testid={`batch-row-${idx}`} className="bg-[#FAFAF7] hover:bg-[#FAFAF7]">
                                        <TableCell colSpan={5} className="py-2">
                                            <BatchPickerRow
                                                li={li}
                                                onChange={(updates) => setLineItems((curr) => {
                                                    const copy = [...curr];
                                                    if (!copy[idx]) return curr;
                                                    copy[idx] = { ...copy[idx], ...updates };
                                                    return copy;
                                                })}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ) : null
                            )}
                        />

                        {/* Discount, Delivery & Logistics */}
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Discount</Label>
                                <div className="flex gap-2">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        data-testid="discount-value"
                                        value={discountValue}
                                        onChange={(e) => setDiscountValue(e.target.value)}
                                        className="bg-[#F5F5F5] flex-1"
                                    />
                                    <Select value={discountType} onValueChange={setDiscountType}>
                                        <SelectTrigger data-testid="discount-type" className="w-28 bg-[#F5F5F5]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="amount">€</SelectItem>
                                            <SelectItem value="percent">%</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Delivery Charge (€, VAT-exempt)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    data-testid="delivery-charge"
                                    value={deliveryCharge}
                                    onChange={(e) => setDeliveryCharge(e.target.value)}
                                    className="bg-[#F5F5F5]"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="flex items-center gap-1.5">
                                    Total Cartons
                                    <span className="text-[10px] text-gray-400 font-normal">(nombre de cartons)</span>
                                </Label>
                                <Input
                                    type="number"
                                    step="1"
                                    min="0"
                                    inputMode="numeric"
                                    placeholder="—"
                                    data-testid="total-cartons"
                                    value={totalCartons}
                                    onChange={(e) => {
                                        // Integer only — strip decimals + negatives client-side
                                        // so warehouse staff can't accidentally type "12.5" or "-3".
                                        const raw = e.target.value.replace(/[^0-9]/g, '');
                                        setTotalCartons(raw);
                                    }}
                                    className="bg-[#F5F5F5]"
                                />
                                <p className="text-[10px] text-gray-400 leading-tight">
                                    Optional. Appears on picking / packing / delivery notes.
                                </p>
                            </div>
                        </div>

                        {/* Invoice-level VAT selector — pre-loaded from the customer's
                            stored profile; operator can override before validation. */}
                        <div className="flex flex-col md:flex-row md:items-end md:justify-end gap-3 mt-6 pt-4 border-t border-[#F5F5F5]">
                            <div className="w-full md:w-80 space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs uppercase tracking-wider text-[#C08478]">
                                        Invoice VAT
                                    </Label>
                                    {selectedCustomerId && selectedCustomerId !== '__new__' && (
                                        <span className="text-[10px] text-gray-400">
                                            from customer profile
                                        </span>
                                    )}
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="col-span-2">
                                        <Select
                                            value={invoiceVatType}
                                            onValueChange={(nextType) => {
                                                setInvoiceVatType(nextType);
                                                if (nextType !== VAT_PROFILE_KEYS.CUSTOM) {
                                                    const nextRate = resolveVatRate(nextType);
                                                    setInvoiceVatRate(nextRate);
                                                    setLineItems((prev) =>
                                                        prev.map((l) => ({ ...l, vat_rate: nextRate })),
                                                    );
                                                }
                                            }}
                                        >
                                            <SelectTrigger data-testid="invoice-vat-type">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {VAT_PROFILES.map((p) => (
                                                    <SelectItem
                                                        key={p.key}
                                                        value={p.key}
                                                        data-testid={`invoice-vat-type-${p.key}`}
                                                    >
                                                        <span className="flex items-baseline gap-2">
                                                            <span>{p.label}</span>
                                                            <span className="text-xs text-gray-400 font-mono">
                                                                {p.rate != null ? `${p.rate}%` : '—'}
                                                            </span>
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            max="30"
                                            data-testid="invoice-vat-rate"
                                            value={invoiceVatRate}
                                            disabled={invoiceVatType !== VAT_PROFILE_KEYS.CUSTOM}
                                            onChange={(e) => {
                                                const next = parseFloat(e.target.value) || 0;
                                                setInvoiceVatRate(next);
                                                setLineItems((prev) =>
                                                    prev.map((l) => ({ ...l, vat_rate: next })),
                                                );
                                            }}
                                            className="font-mono text-right"
                                        />
                                    </div>
                                </div>
                                {invoiceVatType === VAT_PROFILE_KEYS.EU_REVERSE_CHARGE && (
                                    <p className="text-[10px] text-[#C08478] flex items-start gap-1 mt-1">
                                        <span>ⓘ</span>
                                        <span>
                                            Reverse charge — buyer accounts for VAT.
                                            Ensure customer VAT number is on the invoice.
                                        </span>
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Totals */}
                        <div className="flex justify-end mt-4">
                            <div className="w-full md:w-80 space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Subtotal</span>
                                    <span className="font-mono font-medium" data-testid="invoice-subtotal">
                                        {fmt(subtotal)}
                                    </span>
                                </div>
                                {discountAmount > 0 && (
                                    <div className="flex justify-between text-[#E76F51]">
                                        <span>Discount {discountType === 'percent' ? `(${discountValue}%)` : ''}</span>
                                        <span className="font-mono" data-testid="invoice-discount">- {fmt(discountAmount)}</span>
                                    </div>
                                )}
                                {Object.entries(vatBreakdown).length > 1 ? (
                                    Object.entries(vatBreakdown).map(([rate, amt]) => (
                                        <div className="flex justify-between" key={rate}>
                                            <span className="text-gray-500">VAT ({rate}%)</span>
                                            <span className="font-mono font-medium" data-testid={`invoice-vat-${rate}`}>
                                                {fmt(amt)}
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">
                                            VAT ({Object.keys(vatBreakdown)[0] || VAT_RATE}%)
                                        </span>
                                        <span className="font-mono font-medium" data-testid="invoice-vat">
                                            {fmt(vatAmount)}
                                        </span>
                                    </div>
                                )}
                                {delivery > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Delivery</span>
                                        <span className="font-mono font-medium" data-testid="invoice-delivery">
                                            {fmt(delivery)}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between border-t pt-2 text-base">
                                    <span className="font-semibold">Total</span>
                                    <span className="font-mono font-bold text-[#1A1A1A]" data-testid="invoice-total">
                                        {fmt(totalAmount)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Payment - hidden for quotations (only payment required at finalize/invoice creation) */}
                {!isQuotation && (
                    <Card className="border-0 shadow-sm">
                        <CardHeader className="flex flex-row justify-between items-center">
                            <CardTitle className="text-base">Payment</CardTitle>
                            <div className="flex items-center gap-2">
                                <Label htmlFor="split-mode" className="text-sm text-gray-600">
                                    Split Payment
                                </Label>
                                <Switch
                                    id="split-mode"
                                    data-testid="split-payment-toggle"
                                    checked={isSplit}
                                    onCheckedChange={(c) => {
                                        setIsSplit(c);
                                        if (c && payments.length === 0) {
                                            const seedNonce = Math.random().toString(36).slice(2, 7);
                                            setPayments([
                                                { _key: `pmt-seed-1-${Date.now()}-${seedNonce}`, method: '', amount: '', cheques: [] },
                                                { _key: `pmt-seed-2-${Date.now()}-${seedNonce}`, method: '', amount: '', cheques: [] },
                                            ]);
                                        }
                                    }}
                                />
                            </div>
                        </CardHeader>
                        <CardContent>
                            {!isSplit ? (
                                <div className="space-y-4" data-testid="single-payment-section">
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                                        {paymentMethods.map((pm) => {
                                            const Icon = pm.icon;
                                            const selected = paymentMethod === pm.value;
                                            return (
                                                <button
                                                    key={pm.value}
                                                    type="button"
                                                    data-testid={`payment-method-${pm.value}`}
                                                    onClick={() => setPaymentMethod(pm.value)}
                                                    className={`p-3 rounded-xl border-2 transition-all ${
                                                        selected
                                                            ? `${pm.color} text-white border-transparent`
                                                            : 'bg-white border-[#E5E5E5] hover:border-[#C08478] text-[#1A1A1A]'
                                                    }`}
                                                >
                                                    <Icon size={18} className="mx-auto mb-1" />
                                                    <p className="text-xs font-medium">{pm.label}</p>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {paymentMethod === 'cheque' && (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 bg-[#F5F5F5] rounded-xl">
                                            <Input
                                                placeholder="Bank name"
                                                value={chequeBankName}
                                                onChange={(e) => setChequeBankName(e.target.value)}
                                                className="bg-white"
                                            />
                                            <Input
                                                placeholder="Cheque number"
                                                value={chequeNumber}
                                                onChange={(e) => setChequeNumber(e.target.value)}
                                                className="bg-white"
                                            />
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button type="button" variant="outline" className="justify-start bg-white">
                                                        <CalendarIcon size={14} className="mr-2" />
                                                        {chequeDepositDate ? format(chequeDepositDate, 'PP') : 'Deposit date'}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0">
                                                    <Calendar mode="single" selected={chequeDepositDate} onSelect={setChequeDepositDate} />
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-4" data-testid="split-payment-section">
                                    {payments.map((p, i) => (
                                        <div key={p._key || `pmt-${i}`} data-testid={`split-payment-row-${i}`} className="p-4 bg-[#F5F5F5] rounded-xl space-y-3">
                                            <div className="flex items-center justify-between">
                                                <p className="text-sm font-semibold">Payment {i + 1}</p>
                                                {payments.length > 2 && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => removePayment(i)}
                                                        className="text-red-500"
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <Select value={p.method} onValueChange={(v) => updatePayment(i, 'method', v)}>
                                                    <SelectTrigger className="bg-white" data-testid={`split-method-${i}`}>
                                                        <SelectValue placeholder="Method" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {paymentMethods.map((m) => (
                                                            <SelectItem
                                                                key={m.value}
                                                                value={m.value}
                                                                data-testid={`split-method-${i}-option-${m.value}`}
                                                            >
                                                                {m.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="Amount"
                                                    value={p.amount}
                                                    onChange={(e) => updatePayment(i, 'amount', e.target.value)}
                                                    className="bg-white"
                                                    data-testid={`split-amount-${i}`}
                                                />
                                            </div>

                                            {p.method === 'cheque' && (
                                                <div className="space-y-2">
                                                    {p.cheques.map((c, ci) => (
                                                        <div key={ci} className="grid grid-cols-1 md:grid-cols-5 gap-2">
                                                            <Input placeholder="Bank" value={c.bank_name} onChange={(e) => updateCheque(i, ci, 'bank_name', e.target.value)} className="bg-white" />
                                                            <Input placeholder="Cheque #" value={c.cheque_number} onChange={(e) => updateCheque(i, ci, 'cheque_number', e.target.value)} className="bg-white" />
                                                            <Popover>
                                                                <PopoverTrigger asChild>
                                                                    <Button type="button" variant="outline" className="bg-white text-xs justify-start">
                                                                        <CalendarIcon size={12} className="mr-1" />
                                                                        {c.deposit_date ? format(c.deposit_date, 'PP') : 'Date'}
                                                                    </Button>
                                                                </PopoverTrigger>
                                                                <PopoverContent className="w-auto p-0">
                                                                    <Calendar mode="single" selected={c.deposit_date} onSelect={(d) => updateCheque(i, ci, 'deposit_date', d)} />
                                                                </PopoverContent>
                                                            </Popover>
                                                            <Input type="number" step="0.01" placeholder="Amount" value={c.amount} onChange={(e) => updateCheque(i, ci, 'amount', e.target.value)} className="bg-white" />
                                                            <Button type="button" variant="ghost" size="icon" onClick={() => removeCheque(i, ci)}>
                                                                <Trash2 size={14} />
                                                            </Button>
                                                        </div>
                                                    ))}
                                                    <Button type="button" variant="outline" size="sm" onClick={() => addCheque(i)}>
                                                        <Plus size={12} className="mr-1" /> Add cheque
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <Button type="button" variant="outline" onClick={addPayment}>
                                        <Plus size={14} className="mr-1" /> Add Payment
                                    </Button>
                                    <div className="text-sm text-gray-600">
                                        Payments total:{' '}
                                        <span className="font-mono font-bold">
                                            {fmt(payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0))}
                                        </span>{' '}
                                        / {fmt(totalAmount)}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Notes */}
                <Card className="border-0 shadow-sm">
                    <CardContent className="pt-6">
                        <div className="space-y-2">
                            <Label>Notes (optional)</Label>
                            <Textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={2}
                                className="bg-[#F5F5F5]"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Action buttons */}
                <div className="flex justify-end gap-3 flex-wrap">
                    <Button type="button" variant="outline" onClick={() => navigate('/sales')} data-testid="cancel-btn">
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleSaveDraft}
                        disabled={savingDraft || lineItems.length === 0}
                        data-testid="save-draft-btn"
                        className="border-[#C08478] text-[#C08478] hover:bg-[#C08478]/10"
                    >
                        {savingDraft ? <Loader2 className="mr-2 animate-spin" size={14} /> : <Save size={14} className="mr-2" />}
                        Save for Later
                    </Button>
                    {isQuotation ? (
                        <Button
                            type="button"
                            onClick={handleSaveQuotation}
                            disabled={loading || lineItems.length === 0}
                            data-testid="save-quotation-btn"
                            className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
                        >
                            {loading ? <Loader2 className="mr-2 animate-spin" size={14} /> : <FileTextIcon size={14} className="mr-2" />}
                            Save Quotation &amp; Print
                        </Button>
                    ) : (
                        <Button
                            type="submit"
                            data-testid="submit-invoice"
                            disabled={loading || lineItems.length === 0}
                            className="bg-[#1A1A1A] hover:bg-[#C08478] text-white"
                        >
                            {loading ? <Loader2 className="mr-2 animate-spin" size={14} /> : <Printer size={14} className="mr-2" />}
                            {isEditing ? 'Finalize Invoice & Print' : 'Create Invoice & Print'}
                        </Button>
                    )}
                </div>
            </form>

            {/* Mobile sticky action bar — just the primary save CTA.
                Live totals moved into the barcode scanner overlay. */}
            <MobileSaleTotalBar
                primaryLabel={isQuotation ? 'Save Quote' : 'Save Draft'}
                onPrimary={isQuotation ? handleSaveQuotation : handleSaveDraft}
                loading={loading || savingDraft}
                disabled={lineItems.length === 0}
                accentColor={isQuotation ? 'bg-[#8B5CF6]' : 'bg-[#1A1A1A]'}
            />
        </div>
    );
};

// ---------------- BatchPickerRow ----------------
// Extracted to `../components/lineitems/BatchPickerRow.jsx` (Feb 2026 Wave-3).

export default CreateInvoice;
